/**
 * Which drone sounds where (PLAN §1.2): Single, By ring, By direction.
 */
import type { Family, Settings } from '@tonist/shared';
import { sectorOf, type RingIndex } from '../mandala/geometry';

export const SLOT_LABELS: Record<Settings['assignment'], string[]> = {
  single: ['Everywhere'],
  ring: ['Bindu', 'Lotus', 'Palace', 'Vajra'],
  direction: ['East', 'South', 'West', 'North'],
};

/** Quadrant index in E/S/W/N order for an angle (radians, counter-clockwise from east). */
export function quadrantESWN(angle: number): number {
  const q = sectorOf(angle, 4).index; // 0=E 1=N 2=W 3=S (counter-clockwise)
  return [0, 3, 2, 1][q]!;
}

export function droneIdForZone(settings: Pick<Settings, 'assignment' | 'droneIds'>, ring: RingIndex, angle: number): string {
  const ids = settings.droneIds;
  const first = ids[0] ?? 'sa';
  if (settings.assignment === 'single') return first;
  if (settings.assignment === 'ring') return ids[Math.min(ring, 3)] ?? first;
  return ids[quadrantESWN(angle)] ?? first;
}

/** Family colour per ring for the renderer scene (direction mode uses the E/S/W/N families in ring order as a hint). */
export function ringFamilies(settings: Pick<Settings, 'assignment' | 'droneIds'>, familyOf: (id: string) => Family): [Family, Family, Family, Family] {
  const ids = settings.droneIds;
  const f = (i: number) => familyOf(ids[i] ?? ids[0] ?? 'sa');
  if (settings.assignment === 'single') { const x = f(0); return [x, x, x, x]; }
  return [f(0), f(1), f(2), f(3)];
}

/** Unique drone ids the current assignment needs loaded. */
export function activeDroneIds(settings: Pick<Settings, 'assignment' | 'droneIds'>): string[] {
  const n = settings.assignment === 'single' ? 1 : 4;
  const out: string[] = [];
  for (let i = 0; i < n; i++) { const id = settings.droneIds[i] ?? settings.droneIds[0] ?? 'sa'; if (!out.includes(id)) out.push(id); }
  return out;
}
